import { oc } from "@orpc/contract";
import { z } from "zod";
import { OffsetPageInputSchema } from "../../schemas/common";
import {
  SpaRequestStatus,
  SpaUrgency,
  SpaChangeType,
  SpaQuoteType,
} from "@erp/shared-types";

// ─── Enum schemas ────────────────────────────────────────────────────────────

export const SpaRequestStatusSchema = z.nativeEnum(SpaRequestStatus);
export const SpaUrgencySchema = z.nativeEnum(SpaUrgency);
export const SpaChangeTypeSchema = z.nativeEnum(SpaChangeType);

// ─── Response schemas ─────────────────────────────────────────────────────────

export const SpaRequestCommentResponseSchema = z.object({
  id: z.uuid(),
  spaRequestId: z.uuid(),
  authorUserId: z.uuid(),
  authorName: z.string(),
  body: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const SpaRequestAttachmentResponseSchema = z.object({
  id: z.uuid(),
  spaRequestId: z.uuid(),
  documentId: z.uuid(),
  filename: z.string(),
  sizeKb: z.number().int(),
  kind: z.enum(["xlsx", "pdf"]),
  createdAt: z.coerce.date(),
});

export const LinkedSpaResponseSchema = z.object({
  id: z.uuid(),
  spaRequestId: z.uuid().nullable(),
  sourceDocumentId: z.uuid(),
  vendor: z.string(),
  spaNumber: z.string(),
  quoteType: z.nativeEnum(SpaQuoteType).nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  currency: z.string().nullable(),
  customerNumber: z.string().nullable(),
  division: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const SpaRequestResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  requestNumber: z.string(),
  spaNumber: z.string(),
  summary: z.string().nullable(),
  description: z.string().nullable(),
  vendor: z.string(),
  customerNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  division: z.string().nullable(),
  urgency: SpaUrgencySchema,
  changeTypes: z.array(SpaChangeTypeSchema),
  status: SpaRequestStatusSchema,
  createdByUserId: z.uuid(),
  createdByName: z.string(),
  newContractNumber: z.string().nullable(),
  closedAt: z.coerce.date().nullable(),
  spa: LinkedSpaResponseSchema.nullable(),
  attachments: z.array(SpaRequestAttachmentResponseSchema),
  comments: z.array(SpaRequestCommentResponseSchema),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// Lightweight row for the inbox list — no nested comments.
export const SpaRequestListItemSchema = SpaRequestResponseSchema.omit({
  comments: true,
});

// ─── Input schemas ────────────────────────────────────────────────────────────

export const ListSpaRequestsInputSchema = OffsetPageInputSchema.extend({
  status: SpaRequestStatusSchema.optional(),
  search: z.string().optional(),
});

export const GetSpaRequestInputSchema = z.object({
  id: z.uuid(),
});

export const CreateSpaRequestInputSchema = z.object({
  spaNumber: z.string().min(1),
  vendor: z.string().min(1),
  summary: z.string().max(500).optional(),
  description: z.string().max(10000).optional(),
  customerNumber: z.string().optional(),
  customerName: z.string().optional(),
  division: z.string().optional(),
  urgency: SpaUrgencySchema.default(SpaUrgency.Medium),
  changeTypes: z.array(SpaChangeTypeSchema).default([]),
  /** Document IDs of files already uploaded via the document upload endpoint. */
  attachmentDocumentIds: z.array(z.uuid()).default([]),
});

export const AddSpaRequestCommentInputSchema = z.object({
  spaRequestId: z.uuid(),
  body: z.string().min(1).max(10000),
});

export const UpdateStatusInputSchema = z.object({
  id: z.uuid(),
  status: SpaRequestStatusSchema,
});

export const CloseSpaRequestInputSchema = z.object({
  id: z.uuid(),
  newContractNumber: z.string().min(1),
});

export const DeleteSpaRequestInputSchema = z.object({
  id: z.uuid(),
});

// ─── Contracts ────────────────────────────────────────────────────────────────

export const spaRequestsContracts = {
  /**
   * Paginated list of SPA requests for the inbox. Supports status filter and
   * free-text search over spaNumber, summary, customerName, and vendor.
   */
  list: oc
    .input(ListSpaRequestsInputSchema)
    .output(
      z.object({
        items: z.array(SpaRequestListItemSchema),
        total: z.number().int(),
      }),
    )
    .route({
      method: "GET",
      summary: "List SPA requests",
      description:
        "Paginated SPA (special pricing agreement) change requests, filterable by status and free-text search over SPA number, summary, customer name, and vendor.",
    }),

  /** Full SPA request detail, including comments and attachments. */
  get: oc
    .input(GetSpaRequestInputSchema)
    .output(SpaRequestResponseSchema)
    .route({
      method: "GET",
      summary: "Get a SPA request",
      description:
        "Returns one SPA change request with its linked SPA, attachments, and comment thread.",
    }),

  /**
   * Create a new SPA request (status = Draft). Emits SPA_REQUEST_SUBMITTED
   * and kicks off the SPA Change Request workflow.
   */
  create: oc
    .input(CreateSpaRequestInputSchema)
    .output(SpaRequestResponseSchema)
    .route({
      method: "POST",
      summary: "Create a SPA request",
      description:
        "Creates a draft SPA change request for a SPA number and vendor, with customer, urgency, change types and attachments, and starts the SPA change request workflow.",
    }),

  /** Append a comment to a SPA request thread. */
  addComment: oc
    .input(AddSpaRequestCommentInputSchema)
    .output(SpaRequestCommentResponseSchema)
    .route({
      method: "POST",
      summary: "Comment on a SPA request",
      description:
        "Adds a comment to a SPA change request's discussion thread and returns the saved comment.",
    }),

  /**
   * Transition a SPA request to a new status. Requires Action.Approve on
   * Subject.Pricing for the PendingApproval → InProgress transition.
   */
  updateStatus: oc
    .input(UpdateStatusInputSchema)
    .output(SpaRequestResponseSchema)
    .route({
      method: "PATCH",
      summary: "Change a SPA request status",
      description:
        "Moves a SPA change request to a new status. Approving it into in-progress or done requires pricing approval permission.",
    }),

  /**
   * Close a SPA request by recording the new contract number and setting
   * status to Done. Requires Action.Approve on Subject.Pricing.
   */
  close: oc
    .input(CloseSpaRequestInputSchema)
    .output(SpaRequestResponseSchema)
    .route({
      method: "POST",
      summary: "Close a SPA request",
      description:
        "Records the new contract number on a SPA change request and marks it done. Requires pricing approval permission.",
    }),

  /**
   * Soft-delete a SPA request and any workflow-created SPA record linked to it.
   */
  delete: oc
    .input(DeleteSpaRequestInputSchema)
    .output(
      z.object({
        id: z.uuid(),
        deleted: z.literal(true),
        deletedSpaId: z.uuid().nullable(),
      }),
    )
    .route({
      method: "DELETE",
      summary: "Delete a SPA request",
      description:
        "Soft-deletes a SPA change request and any SPA record its workflow created. Requires pricing delete permission.",
    }),
} as const;

// ─── Exported types ───────────────────────────────────────────────────────────

export type SpaRequestResponse = z.infer<typeof SpaRequestResponseSchema>;
export type SpaRequestListItem = z.infer<typeof SpaRequestListItemSchema>;
export type SpaRequestCommentResponse = z.infer<
  typeof SpaRequestCommentResponseSchema
>;
export type SpaRequestAttachmentResponse = z.infer<
  typeof SpaRequestAttachmentResponseSchema
>;
export type LinkedSpaResponse = z.infer<typeof LinkedSpaResponseSchema>;
export type CreateSpaRequestInput = z.infer<typeof CreateSpaRequestInputSchema>;
export type UpdateStatusInput = z.infer<typeof UpdateStatusInputSchema>;
export type CloseSpaRequestInput = z.infer<typeof CloseSpaRequestInputSchema>;
export type DeleteSpaRequestInput = z.infer<typeof DeleteSpaRequestInputSchema>;
